<template>
  <div class="callback-container">
    <div v-if="isLoading" class="spinner-wrapper">
      <div class="spinner"></div>
      <p>Please wait, authenticating with YouTube...</p>
    </div>
    <div v-if="error" class="error-message">
      <p>Authentication failed: {{ error }}</p>
      <router-link to="/stream">Return to Clerk Screen</router-link>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted} from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { youtubeAuthUrl } from '@/helpers/endpoints';
import {getStoredToken} from "@/store/plugins/websocket/createConnection";

export default defineComponent({
  name: 'YouTubeCallback',
  props: {
    code: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const isLoading = ref(true);
    const error = ref<string | null>(null);

    const handleCallback = async () => {
      if (!props.code) {
        error.value = 'Invalid callback parameters from Google.';
        isLoading.value = false;
        return;
      }

      try {
        await axios.post(youtubeAuthUrl(), { code: props.code }, {
          headers: { "dar-client-token": getStoredToken() }
        });

        // On success, redirect back to the page that initiated the auth flow
        const redirectPath = localStorage.getItem('youtubeAuthRedirect');
        localStorage.removeItem('youtubeAuthRedirect');

        if (redirectPath) {
          router.push(redirectPath);
        } else {
          // Fallback location
          router.push({ name: 'Streamer' });
        }
      } catch (err: any) {
        error.value = err.response?.data?.message || 'Failed to exchange authorization code.';
        console.error('Error handling YouTube callback:', err);
        isLoading.value = false;
      }
    };

    onMounted(() => {
      handleCallback();
    });

    return {
      isLoading,
      error,
    };
  },
});
</script>

<style scoped>
.callback-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 1.2rem;
  color: #555;
  background-color: #f0f2f5;
}
.spinner-wrapper {
  text-align: center;
}
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #09f;
  margin-bottom: 20px;
  animation: spin 1s ease infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.error-message {
  padding: 20px;
  background-color: #fff3f3;
  border: 1px solid #dc3545;
  border-radius: 8px;
  color: #dc3545;
  text-align: center;
}
</style>
